import { useState, useCallback, useEffect } from "react";
import { Form } from "antd";
import { TableRecord } from "types/table.types";

export const useEditableTable = <T extends TableRecord>(
  initialData: T[],
  onChangeData?: (newData: T[]) => void,
  onSaveCallback?: (key: string | number, record: T) => void
) => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<T[]>(initialData);
  const [editingKey, setEditingKey] = useState<string | number>("");
  const [isNewRow, setIsNewRow] = useState(false);
  const [originalRecord, setOriginalRecord] = useState<T | null>(null); // Lưu data gốc để rollback
  useEffect(() => {
    setDataSource(initialData);
  }, [initialData]);
  const isEditing = useCallback(
    (record: T) => record.key === editingKey,
    [editingKey]
  );

  const edit = useCallback(
    (record: T) => {
      setOriginalRecord({ ...record });
      form.setFieldsValue({ ...record });
      setEditingKey(record.key);
      setIsNewRow(false);
    },
    [form]
  );

  const cancel = useCallback(() => {
    if (isNewRow) {
      const newData = dataSource.filter((item) => item.key !== editingKey);
      setDataSource(newData);
      onChangeData?.(newData);
    } else if (originalRecord) {
      const newData = dataSource.map((item) =>
        item.key === editingKey ? originalRecord : item
      );
      setDataSource(newData);
      onChangeData?.(newData);
    }
    setEditingKey("");
    setIsNewRow(false);
    setOriginalRecord(null);
    form.resetFields();
  }, [isNewRow, editingKey, dataSource, onChangeData, form, originalRecord]);

  const save = useCallback(
    async (key: string | number) => {
      try {
        const row = (await form.validateFields()) as T;
        const newData = [...dataSource];
        const index = newData.findIndex((item) => key === item.key);

        if (index > -1) {
          const item = newData[index];
          const updatedItem = { ...item, ...row };
          newData.splice(index, 1, updatedItem);
          setDataSource(newData);
          setEditingKey("");
          setIsNewRow(false);
          setOriginalRecord(null);

          onChangeData?.(newData);
          onSaveCallback?.(key, updatedItem);
          return true;
        }
        return false;
      } catch (errInfo) {
        console.error("Validate Failed:", errInfo);
        return false;
      }
    },
    [dataSource, form, onChangeData, onSaveCallback]
  );

  const addRow = useCallback(
    async (newRow: Partial<T>) => {
      if (editingKey) {
        const saved = await save(editingKey);
        if (!saved) {
          console.log(
            "Please complete or cancel the current row before adding a new one"
          );
          return false;
        }
      }

      const newRecord = {
        key: `new_${Date.now()}`,
        ...newRow,
      } as T;

      const newData = [...dataSource, newRecord];
      setDataSource(newData);
      onChangeData?.(newData);

      setTimeout(() => {
        setOriginalRecord(null);
        form.resetFields();
        form.setFieldsValue({ ...newRecord });
        setEditingKey(newRecord.key);
        setIsNewRow(true);

        requestAnimationFrame(() => {
          form.validateFields().catch(() => { });
        });
      }, 0);

      return true;
    },
    [dataSource, onChangeData, form, editingKey, save]
  );

  const deleteRow = useCallback(
    (key: string | number) => {
      const newData = dataSource.filter((item) => item.key !== key);
      setDataSource(newData);
      onChangeData?.(newData);

      if (key === editingKey) {
        setEditingKey("");
        setIsNewRow(false);
        setOriginalRecord(null);
        form.resetFields();
      }
    },
    [dataSource, onChangeData, editingKey, form]
  );

  const handleDoubleClick = useCallback(
    (record: T) => {
      if (!isEditing(record)) {
        edit(record);
      }
    },
    [edit, isEditing]
  );

  return {
    form,
    dataSource,
    setDataSource,
    isEditing,
    edit,
    cancel,
    save,
    addRow,
    deleteRow,
    handleDoubleClick,
  };
};
